import React, { useEffect } from "react";
import Header from "../home/header";
import { useState } from "react";
import { useNavigate } from "react-router";
import UTILS from "../../utils/commons";
import Loading from "../home/Loading";
import { ApiClient } from "../../utils/api-client";
import { useFormik } from 'formik';
import * as Yup from 'yup';

const ReceiveInventory = props => {
  const loginState = JSON.parse( window.sessionStorage.getItem( "loginState" ) );
  const apiClient = new ApiClient( loginState ? loginState.accessToken : null );
  const [ isLoading, setIsLoading ] = useState( false );
  const [ feedback, setFeedback ] = useState( "" );
  const [ profitMargin, setProfitMargin ] = useState( UTILS.PROFIT_MARGIN );
  const [ products, setProducts ] = useState( [] );
  const [ suppliers, setSuppliers ] = useState( [] );
  const [ selectedSupplier, setSelectedSupplier ] = useState();
  const [ selectedProduct, setSelectedProduct ] = useState();
  const [ invoiceFile, setInvoiceFile ] = useState( null );
  const labelClass = "mb-1 block text-sm font-medium text-gray-700";
  const controlClass = "form-control h-11 rounded-md border-gray-300";
  // Helper to format number with commas
  const formatNumberWithCommas = ( value ) => {
    if ( value === null || value === undefined ) return '';
    const parts = value.toString().split( "." );
    parts[ 0 ] = parts[ 0 ].replace( /\B(?=(\d{3})+(?!\d))/g, "," );
    return parts.join( "." );
  };

  // Formik setup
  const DEFAULT_PROFIT_MARGIN = 30;
  const formik = useFormik( {
    initialValues: {
      supplier: '',
      product: '',
      quantity: '',
      invoice: '',
      amountPaid: '',
      cif: '',
      tpri: '',
      clearing: '',
      buying_price: '',
      profit_margin: DEFAULT_PROFIT_MARGIN.toString(),
      selling_price: '',
    },
    validationSchema: Yup.object( {
      supplier: Yup.string().required( 'Supplier is required' ),
      product: Yup.string().required( 'Product is required' ),
      quantity: Yup.string().required( 'Quantity is required' ).matches( /^\d+$/, 'Must be a number' ),
      invoice: Yup.string().required( 'Invoice number is required' ),
      amountPaid: Yup.string().required( 'Amount is required' ).matches( /^\d+(\.\d{1,2})?$/, 'Must be a valid number' ),
      cif: Yup.string().required( 'CIF is required' ).matches( /^\d+(\.\d{1,2})?$/, 'Must be a valid number' ),
      tpri: Yup.string().required( 'TPHPA Cost is required' ).matches( /^\d+(\.\d{1,2})?$/, 'Must be a valid number' ),
      clearing: Yup.string().required( 'Clearing Charges is required' ).matches( /^\d+(\.\d{1,2})?$/, 'Must be a valid number' ),
      profit_margin: Yup.string().required( 'Profit margin is required' ).matches( /^\d+(\.\d{1,2})?$/, 'Must be a valid number' ),
      selling_price: Yup.string().required( 'Selling price is required' ).matches( /^\d+(\.\d{1,2})?$/, 'Must be a valid number' ),
    } ),
    onSubmit: async ( values ) => {
      setIsLoading( true );
      // Remove commas for backend
      const clean = ( v ) => v.replace( /,/g, '' );
      let inventory = {
        type: 'receive',
        profitMargin: parseFloat( clean( values.profit_margin ) ),
        productId: values.product,
        supplierId: values.supplier,
        invoiceNumber: values.invoice,
        amountPaid: parseFloat( values.amountPaid ),
        stockQuantity: parseInt( clean( values.quantity ) ),
        cifCost: parseFloat( clean( values.cif ) ),
        tpriCost: parseFloat( clean( values.tpri ) ),
        clearingCost: parseFloat( clean( values.clearing ) ),
        buyingPrice: parseFloat( clean( values.buying_price ) ),
        sellingPrice: parseFloat( clean( values.selling_price ) ),
        invoiceFile: invoiceFile
      };
      const result = await saveInventory( inventory );
      setIsLoading( false );
      if ( result ) {
        navigate( '/inventory', { replace: true } );
      }
    },
  } );
  const [ quantity, setQuantity ] = useState( 1 );


  let navigate = useNavigate();

  const saveInventory = async ( inv ) => {
    setIsLoading( true );
    try {
      const res = await apiClient.post( 'inventory', inv );
      setIsLoading( false )
      navigate( '/inventory', { replace: true } );
    } catch ( e ) {
      setFeedback( e );
      // setIsLoading(false)
      return null;
    }

  }
  const loadData = async () => {
    const [ supplierRes, productRes ] = await Promise.all( [
      apiClient.get( 'suppliers' ),
      apiClient.get( 'products' )
    ] );
    setSuppliers( supplierRes.data.data );
    setProducts( productRes.data.data );
    if ( productRes.data.data.length > 0 ) {
      setSelectedProduct( productRes.data.data[ 0 ]?.id );
    }
    if ( supplierRes.data.data.length > 0 ) {
      setSelectedSupplier( supplierRes.data.data[ 0 ]?.id );
    }
  }
  const cleanInvoiceNumber = ( invoice ) => {
    if ( invoice.includes( '/' ) ) {
      return invoice.replaceAll( '/', '-' ).trim();
    }
    else return invoice.trim();
  }


  // General handler for all amount fields (Formik)
  // Update buying and selling price when relevant fields change
  const updatePrices = ( fields ) => {
    // Remove commas and parse
    const clean = ( v ) => v ? parseFloat( v.replace( /,/g, '' ) ) : 0;
    const cif = clean( fields.cif );
    const tpri = clean( fields.tpri );
    const clearing = clean( fields.clearing );
    const profitMargin = clean( fields.profit_margin );
    const quantity = clean( fields.quantity );
    let buyingPrice = 0;
    if ( quantity > 0 ) {
      buyingPrice = ( cif + tpri + clearing ) / quantity;
    }
    // Calculate selling price
    const sellingPrice = buyingPrice * ( 1 + profitMargin / 100 );
    return {
      buying_price: buyingPrice && isFinite( buyingPrice ) ? buyingPrice.toFixed( 2 ) : '',
      selling_price: buyingPrice && isFinite( sellingPrice ) ? sellingPrice.toFixed( 2 ) : '',
    };
  };

  const handleAmountChange = ( e ) => {
    let t = e.target;
    let raw = t.value.replace( /,/g, '' );
    // Only allow numbers and optional decimal
    if ( !/^\d*(\.\d*)?$/.test( raw ) ) return;
    formik.setFieldValue( t.name, raw );

    // If the field is one of cif, tpri, clearing, profit_margin, or quantity, update prices
    if ( [ "cif", "tpri", "clearing", "profit_margin", "quantity", "amountPaid" ].includes( t.name ) ) {
      setTimeout( () => {
        const fields = {
          cif: t.name === "cif" ? raw : formik.values.cif,
          tpri: t.name === "tpri" ? raw : formik.values.tpri,
          clearing: t.name === "clearing" ? raw : formik.values.clearing,
          profit_margin: t.name === "profit_margin" ? raw : formik.values.profit_margin,
          quantity: t.name === "quantity" ? raw : formik.values.quantity,
          // amountPaid: t.name === "amountPaid" ? raw : formik.values.amountPaid
        };
        const { buying_price, selling_price } = updatePrices( fields );
        formik.setFieldValue( 'buying_price', buying_price );
        formik.setFieldValue( 'selling_price', selling_price );
      }, 0 );
    }
  };
  const handleChangeFile = e => {
    e.preventDefault();
    const file = e.target.files[ 0 ];
    const fileReader = new FileReader();
    fileReader.onload = () => {
      const fileDataUrl = fileReader.result;
      setInvoiceFile( fileDataUrl );
    };
    fileReader.readAsDataURL( file );
  }
  const handleSupplierChange = ( e ) => {
    formik.setFieldValue( 'supplier', e.target.value );
    setSelectedSupplier( e.target.value );
  }
  const handleProductChange = ( e ) => {
    formik.setFieldValue( 'product', e.target.value );
    setSelectedProduct( e.target.value );
  }
  useEffect( () => {
    if ( !loginState ) {
      navigate( '/login', { replace: true, target: "/inventory/receive" } );
    }
    else loadData();
  }, [] )
  return ( <>
    <Header auth={loginState} />
    <section className="flex justify-start items-start">
      <div className="container mx-auto">
        <div className="rounded-xl border border-gray-200 bg-gradient-to-r from-white to-gray-50 p-4 md:p-6 shadow-sm">
          {feedback ? <div className="w-full rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-center text-blue-700">{String( feedback?.message || feedback )}</div> : null}
          <div className="mb-4">
            <p className="text-sm uppercase tracking-wide text-gray-500">Stock</p>
            <p className="text-2xl font-semibold text-gray-800">Receive Inventory</p>
          </div>
          <form id="receive_form"
            className="w-full md:w-11/12 lg:w-10/12 mx-auto"
            method="post" onSubmit={formik.handleSubmit}
          >
            <div className="rounded-lg border border-gray-200 bg-white p-4 md:p-5">
              <p className="text-xs uppercase tracking-wide text-gray-500 mb-3">Supplier and Product</p>
              <div className="row">
                <div className="form-group my-2 md:w-6/12 w-10/12">
                  <label className={labelClass} htmlFor="supplier">Select Supplier</label>
                  <select
                    onChange={handleSupplierChange}
                    value={formik.values.supplier}
                    className={controlClass}
                    id="supplier"
                    name="supplier"><option value="" disabled>-- Select Supplier --</option>
                    {suppliers.map( ( s ) => {
                      return <option key={UTILS.randomKey()} value={s.id}>{s.companyName}</option>
                    } )}
                  </select>
                  {formik.touched.supplier && formik.errors.supplier ? (
                    <div className="text-danger small">{formik.errors.supplier}</div>
                  ) : null}

                </div>
                <div className="form-group my-2  md:w-6/12 w-10/12">
                  <label className={labelClass} htmlFor="product">Select Product</label>
                  <select
                    className={controlClass}
                    id="product" value={formik.values.product} onChange={handleProductChange}
                    name="product"><option value="" disabled>-- Select Product --</option>
                    {products.map( p => {
                      return <option key={p.id} value={p.id}>{p.productName + " (" + p.packSize + ")"}</option>
                    } )}
                  </select>
                  {formik.touched.product && formik.errors.product ? (
                    <div className="text-danger small">{formik.errors.product}</div>
                  ) : null}
                </div>

              </div>
            </div>
            <div className="row">

              <div className="form-group my-2  md:w-6/12 w-10/12">

                <label className={labelClass} htmlFor="quantity">Quantity</label>
                <input
                  type="text" required
                  className={controlClass}
                  id="quantity"
                  name="quantity"
                  placeholder="Number of cartons"
                  value={formatNumberWithCommas( formik.values.quantity )}
                  onChange={handleAmountChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.quantity && formik.errors.quantity ? (
                  <div className="text-danger small">{formik.errors.quantity}</div>
                ) : null}
              </div>
              <div className="form-group my-2  md:w-6/12 w-10/12">
                <label className={labelClass} htmlFor="invoice">Invoice Number</label>
                <input
                  type="text" required
                  className={controlClass}
                  id="invoice"
                  name="invoice"
                  placeholder="Invoice number"
                  value={formik.values.invoice}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.invoice && formik.errors.invoice ? (
                  <div className="text-danger small">{formik.errors.invoice}</div>
                ) : null}
              </div>

            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4 md:p-5 mt-4">
              <p className="text-xs uppercase tracking-wide text-gray-500 mb-3">Invoice and Costs</p>
              <div className="row">
                <div className="form-group my-2 md:w-6/12 w-10/12">
                  <label className={labelClass} htmlFor="invoice_file">Upload Invoice</label>
                  <input className={controlClass} type="file" id="invoice_file" name="invoice_file" onChange={handleChangeFile} />
                </div>
                <div className="form-group my-2 md:w-6/12 w-10/12">
                  <label className={labelClass} htmlFor="amountPaid">Amount Paid</label>
                  <input className={controlClass} type="text" id="amountPaid" name="amountPaid" placeholder="Amount paid to supplier in TZS"
                    value={formatNumberWithCommas( formik.values.amountPaid )}
                    onChange={handleAmountChange}
                    onBlur={formik.handleBlur} />
                </div>
              </div>

              <div className="row">
                <div className="form-group my-2  md:w-6/12 w-10/12">
                  <label className={labelClass} htmlFor="cif">CIF</label>
                  <input
                    type="text" required
                    className={controlClass}
                    id="cif"
                    name="cif"
                    placeholder="CIF"
                    value={formatNumberWithCommas( formik.values.cif )}
                    onChange={handleAmountChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.cif && formik.errors.cif ? (
                    <div className="text-danger small">{formik.errors.cif}</div>
                  ) : null}
                </div>
                <div className="form-group my-2  md:w-6/12 w-10/12">
                  <label className={labelClass} htmlFor="tpri">TPHPA Cost</label>
                  <input
                    type="text" required
                    className={controlClass}
                    id="tpri"
                    name="tpri"
                    placeholder="TPHPA Costs"
                    value={formatNumberWithCommas( formik.values.tpri )}
                    onChange={handleAmountChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.tpri && formik.errors.tpri ? (
                    <div className="text-danger small">{formik.errors.tpri}</div>
                  ) : null}
                </div>
              </div>
              <div className="row">
                <div className="form-group my-2 md:w-6/12 w-10/12">
                  <label className={labelClass} htmlFor="clearing">Clearing Charges</label>
                  <input
                    type="text" required
                    className={controlClass}
                    id="clearing"
                    name="clearing"
                    placeholder="Clearing costs"
                    value={formatNumberWithCommas( formik.values.clearing )}
                    onChange={handleAmountChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.clearing && formik.errors.clearing ? (
                    <div className="text-danger small">{formik.errors.clearing}</div>
                  ) : null}
                </div>
                <div className="form-group my-2 md:w-6/12 w-10/12">
                  <label className={labelClass} htmlFor="buying_price">Buying Price</label>
                  <input
                    type="text" disabled
                    className={controlClass}
                    id="buying_price"
                    name="buying_price"
                    placeholder="Buying price"
                    value={formatNumberWithCommas( formik.values.buying_price )}
                    readOnly
                  />
                </div>
              </div>
              <div className="row">
                <div className="form-group my-2 md:w-6/12 w-10/12">
                  <label className={labelClass} htmlFor="profit_margin">Profit Margin (%)</label>
                  <input
                    type="text"
                    className={controlClass}
                    id="profit_margin"
                    name="profit_margin"
                    placeholder="Profit Margin"
                    value={formatNumberWithCommas( formik.values.profit_margin )}
                    onChange={handleAmountChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.profit_margin && formik.errors.profit_margin ? (
                    <div className="text-danger small">{formik.errors.profit_margin}</div>
                  ) : null}
                </div>
                <div className="form-group my-2 md:w-6/12 w-10/12">
                  <label className={labelClass} htmlFor="selling_price">Selling Price</label>
                  <input
                    type="text"
                    className={controlClass}
                    id="selling_price"
                    name="selling_price"
                    placeholder="Selling Price"
                    value={formatNumberWithCommas( formik.values.selling_price )}
                    onChange={handleAmountChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.selling_price && formik.errors.selling_price ? (
                    <div className="text-danger small">{formik.errors.selling_price}</div>
                  ) : null}
                </div></div>
            </div>
            <div className="form-group my-2 row mt-4">
              <div className="my-2 md:w-6/12 w-10/12">
                {isLoading ? <Loading /> :
                  <input
                    type="submit"
                    className="form-control h-11 rounded-md bg-success md:w-5/12 w-10/12 text-white font-semibold"
                    id="btnSubmit"
                    name="btnSubmit"
                    value="SUBMIT"
                  />}
              </div>
              <div className="my-2 md:w-6/12 w-10/12">
                <input onClick={() => { navigate( -1, { replace: true } ) }}
                  type="button"
                  className="form-control h-11 rounded-md md:w-5/12 w-10/12"
                  value="Cancel"
                />
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
  </> )

}
export default ReceiveInventory;
